import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { strapiService } from '../../utils/api';
import AlertService from '../../utils/alertService';
import {
  Button, Card, EmptyState, KpiCard, Note, SearchInput, Select, SkeletonPage, Sym,
  useCompany, usePageTitle,
} from '../../components/app2';
import PeriodRow from './components/PeriodRow';
import ReceiptRow from './components/ReceiptRow';

/**
 * Recibos de la empresa (jul-2026).
 *
 * Nace de un hallazgo del cliente: el menú "Portal de empleado" llevaba a la
 * pantalla de autoservicio y, sin empleado elegido, mostraba a UNO cualquiera
 * —el primero de la lista— y reventaba con 403. Un administrador no quiere ver
 * el portal de una persona: quiere los recibos de TODA su empresa.
 *
 * Se agrupa por periodo de nómina porque así se piensa la nómina ("la quincena
 * del 1 al 15"): una lista plana de miles de renglones es inservible. Al buscar
 * a alguien la vista cambia sola a lista, que es cuando esa forma sí sirve.
 */

const money = (n) => new Intl.NumberFormat('es-MX', {
  style: 'currency', currency: 'MXN', maximumFractionDigits: 2,
}).format(Number(n) || 0);

const CompanyReceipts = () => {
  const { idEmpresaActiva, empresaActiva } = useCompany();

  const [periodos, setPeriodos] = useState([]);
  const [años, setAños] = useState([]);
  const [año, setAño] = useState('');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [limitadoPorPlan, setLimitadoPorPlan] = useState(false);

  // Búsqueda: al escribir, la pantalla deja de agrupar y muestra coincidencias.
  const [busqueda, setBusqueda] = useState('');
  const [terminoAplicado, setTerminoAplicado] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);

  usePageTitle({
    title: empresaActiva ? (empresaActiva.name || empresaActiva.businessName) : null,
    kicker: 'Operación',
  });

  // ── Periodos ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!idEmpresaActiva) return undefined;
    let cancelado = false;

    (async () => {
      try {
        setCargando(true);
        setError(null);
        const r = await strapiService.payroll.getCompanyReceiptPeriods(idEmpresaActiva, { year: año || undefined });
        if (cancelado) return;
        setPeriodos(Array.isArray(r?.data) ? r.data : []);
        setLimitadoPorPlan(Boolean(r?.meta?.planLimited));
        if (Array.isArray(r?.meta?.years) && r.meta.years.length) setAños(r.meta.years);
      } catch (e) {
        if (cancelado) return;
        console.error('Error cargando los periodos de nómina:', e);
        setError('No se pudieron cargar los recibos de la empresa.');
      } finally {
        if (!cancelado) setCargando(false);
      }
    })();

    return () => { cancelado = true; };
  }, [idEmpresaActiva, año]);

  // ── Búsqueda (se dispara sola tras dejar de escribir) ────────────────────────
  useEffect(() => {
    const termino = busqueda.trim();
    if (termino.length < 2) {
      setTerminoAplicado('');
      setResultados([]);
      return undefined;
    }

    const t = setTimeout(async () => {
      try {
        setBuscando(true);
        const r = await strapiService.payroll.getCompanyReceipts(idEmpresaActiva, {
          search: termino, year: año || undefined, limit: 200,
        });
        setResultados(Array.isArray(r?.data) ? r.data : []);
        setTerminoAplicado(termino);
      } catch (e) {
        console.error('Error buscando recibos:', e);
        AlertService.error('Error', 'No se pudo completar la búsqueda.');
      } finally {
        setBuscando(false);
      }
    }, 350);

    return () => clearTimeout(t);
  }, [busqueda, idEmpresaActiva, año]);

  const cargarRecibosDePeriodo = useCallback(async (periodo) => {
    const r = await strapiService.payroll.getCompanyReceipts(idEmpresaActiva, {
      periodStart: String(periodo.periodStart).slice(0, 10),
      periodEnd: String(periodo.periodEnd).slice(0, 10),
      limit: 500,
    });
    return Array.isArray(r?.data) ? r.data : [];
  }, [idEmpresaActiva]);

  const totales = useMemo(() => periodos.reduce((acc, p) => ({
    recibos: acc.recibos + (p.receipts || 0),
    neto: acc.neto + (Number(p.netAmount) || 0),
  }), { recibos: 0, neto: 0 }), [periodos]);

  if (cargando) return <SkeletonPage kpis={3} rows={6} />;

  if (error) {
    return (
      <EmptyState
        icon="error"
        size="lg"
        title="No se pudieron cargar los recibos"
        text={error}
        action={<Button variant="outline" icon="refresh" onClick={() => window.location.reload()}>Reintentar</Button>}
      />
    );
  }

  const buscandoAlgo = terminoAplicado.length >= 2;

  return (
    <div className="a2-anim-screen" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 16 }}>
      {/* Resumen */}
      <div className="a2-grid-kpi">
        <KpiCard icon="receipt_long" label="Recibos" value={totales.recibos.toLocaleString('es-MX')} />
        <KpiCard icon="event_repeat" label="Periodos de nómina" value={periodos.length} />
        <KpiCard icon="payments" label="Neto pagado" value={money(totales.neto)} />
      </div>

      {limitadoPorPlan ? (
        <Note tone="warn" icon="lock">
          <span>
            <strong style={{ color: 'var(--heading)' }}>Vista limitada por tu plan.</strong>{' '}
            Solo se muestran los recibos de los colaboradores incluidos en tu plan actual.
          </span>
        </Note>
      ) : null}

      <Card pad={false} style={{ padding: 'clamp(18px,2.5vw,24px)' }}>
        {/* Buscador + año */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 18 }}>
          <SearchInput
            value={busqueda}
            onChange={(e) => setBusqueda(e?.target?.value)}
            placeholder="Buscar por nombre, RFC, folio o UUID…"
            aria-label="Buscar recibos"
            style={{ flex: 1, minWidth: 260 }}
          />
          <Select
            value={año}
            onChange={(e) => setAño(e?.target?.value)}
            aria-label="Filtrar por año"
            style={{ width: 'auto', minWidth: 130 }}
          >
            <option value="">Todos los años</option>
            {años.map((a) => <option key={a} value={a}>{a}</option>)}
          </Select>
        </div>

        {buscandoAlgo ? (
          /* ── Resultados de búsqueda: lista plana ─────────────────────────── */
          <div>
            <div style={{ font: "600 .8rem 'Hanken Grotesk'", color: 'var(--muted)', marginBottom: 10 }}>
              {buscando ? 'Buscando…' : `${resultados.length} recibo(s) para «${terminoAplicado}»`}
            </div>
            {!buscando && resultados.length === 0 ? (
              <EmptyState
                icon="search_off"
                title="Sin coincidencias"
                text="No hay recibos que coincidan con la búsqueda. Prueba con el nombre, el RFC o el folio."
              />
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 4 }}>
                {resultados.map((r) => <ReceiptRow key={r.id} receipt={r} mostrarPeriodo />)}
              </div>
            )}
          </div>
        ) : periodos.length === 0 ? (
          <EmptyState
            icon="receipt_long"
            title="Aún no hay recibos"
            text="Cuando sincronices con el SAT, los recibos timbrados aparecerán agrupados por periodo de nómina."
          />
        ) : (
          /* ── Vista normal: periodos de nómina ────────────────────────────── */
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 6 }}>
            {periodos.map((p) => (
              <PeriodRow
                key={`${p.periodStart}-${p.periodEnd}`}
                periodo={p}
                cargarRecibos={cargarRecibosDePeriodo}
              />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default CompanyReceipts;
